import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { InboxAudience, InboxMessageStatus, InboxMessageType } from '@app/interfaces/inboxMessage';

export interface InboxMessagesFilterState {
  audience: InboxAudience;
  shouldTriggerFetch: boolean;
  multiSearch: string;
  selectedStatuses: InboxMessageStatus[];
  selectedStatusesTemp: InboxMessageStatus[];
  selectedTypes: InboxMessageType[];
  selectedTypesTemp: InboxMessageType[];
  sendingDateFrom: string;
  sendingDateFromTemp: string;
}

const initialState: InboxMessagesFilterState = {
  audience: InboxAudience.Fishers,
  shouldTriggerFetch: true,
  multiSearch: '',
  selectedStatuses: [],
  selectedStatusesTemp: [],
  selectedTypes: [],
  selectedTypesTemp: [],
  sendingDateFrom: '',
  sendingDateFromTemp: '',
};

const resetFilterValues = (state: InboxMessagesFilterState) => {
  state.multiSearch = initialState.multiSearch;
  state.selectedStatuses = initialState.selectedStatuses;
  state.selectedStatusesTemp = initialState.selectedStatusesTemp;
  state.selectedTypes = initialState.selectedTypes;
  state.selectedTypesTemp = initialState.selectedTypesTemp;
  state.sendingDateFrom = initialState.sendingDateFrom;
  state.sendingDateFromTemp = initialState.sendingDateFromTemp;
};

const inboxMessagesFiltersSlice = createSlice({
  name: 'inboxMessagesFilters',
  initialState,
  reducers: {
    setAudience(state, action: PayloadAction<InboxAudience>) {
      if (state.audience === action.payload) {
        return;
      }
      state.audience = action.payload;
      resetFilterValues(state);
      state.shouldTriggerFetch = true;
    },
    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },
    setMultiSearch(state, action: PayloadAction<string>) {
      state.multiSearch = action.payload;
    },
    setSelectedStatuses(state, action: PayloadAction<InboxMessageStatus[]>) {
      state.selectedStatuses = action.payload;
    },
    setSelectedStatusesTemp(state, action: PayloadAction<InboxMessageStatus[]>) {
      state.selectedStatusesTemp = action.payload;
    },
    setSelectedTypes(state, action: PayloadAction<InboxMessageType[]>) {
      state.selectedTypes = action.payload;
    },
    setSelectedTypesTemp(state, action: PayloadAction<InboxMessageType[]>) {
      state.selectedTypesTemp = action.payload;
    },
    setSendingDateFrom(state, action: PayloadAction<string>) {
      state.sendingDateFrom = action.payload;
    },
    setSendingDateFromTemp(state, action: PayloadAction<string>) {
      state.sendingDateFromTemp = action.payload;
    },
    resetFilters(state) {
      resetFilterValues(state);
      state.shouldTriggerFetch = true;
    },
  },
});

export const {
  setAudience,
  setShouldTriggerFetch,
  setMultiSearch,
  setSelectedStatuses,
  setSelectedStatusesTemp,
  setSelectedTypes,
  setSelectedTypesTemp,
  setSendingDateFrom,
  setSendingDateFromTemp,
  resetFilters,
} = inboxMessagesFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { inboxMessagesFilters: InboxMessagesFilterState }) => {
  const { multiSearch, selectedStatuses, selectedTypes, sendingDateFrom } = state.inboxMessagesFilters;

  return !!multiSearch || !!selectedStatuses.length || !!selectedTypes.length || !!sendingDateFrom;
};

export const activeFiltersCountSelector = (state: { inboxMessagesFilters: InboxMessagesFilterState }) => {
  const { multiSearch, selectedStatuses, selectedTypes, sendingDateFrom } = state.inboxMessagesFilters;

  let count = 0;
  if (multiSearch) count++;
  if (selectedStatuses.length > 0) count++;
  if (selectedTypes.length > 0) count++;
  if (sendingDateFrom) count++;

  return count;
};

export const fetchParamsSelector = (state: { inboxMessagesFilters: InboxMessagesFilterState }) => {
  const { multiSearch, selectedStatuses, selectedTypes, sendingDateFrom } = state.inboxMessagesFilters;

  return {
    multiSearch,
    selectedStatuses,
    selectedTypes,
    sendingDateFrom,
  };
};

export const audienceSelector = (state: { inboxMessagesFilters: InboxMessagesFilterState }) =>
  state.inboxMessagesFilters.audience;

export const inboxMessagesFiltersActions = inboxMessagesFiltersSlice.actions;

export const inboxMessagesFilters = inboxMessagesFiltersSlice.reducer;
